import {
  CRISIS_DIRECTOR,
  MEGASTRUCTURE,
  VICTORY,
  type CountdownVictoryId,
  type FactionId,
} from '@sd/content';
import { t } from '@sd/i18n';
import { atWar, isSanctioned, standings, windowOutput, type GameState } from '@sd/sim';
import type { Viewer } from '../map/describe';
import { clock, num, playerName } from './format';

/**
 * The race for victory and the players (#56, the left column): who stands
 * how far from each way to win, what holds them back, and how the others
 * stand towards the player. Pure, tested without a browser.
 */
export interface RaceRow {
  readonly id: 'economic' | 'domination' | 'technological' | 'joint';
  readonly title: string;
  /** The player's own standing in words. */
  readonly status: string;
  /** Each player's progress towards the goal, 0..1 (the goal is 1). */
  readonly progress: readonly { readonly playerId: string; readonly value: number }[];
  readonly note: string;
  /** A rival's countdown or something stopping the player's own: drawn as a warning. */
  readonly alert: boolean;
}

const TIER3 = ['nanofactories', 'interstellarFinance', 'scannerMk3'] as const;

export function raceRows(state: GameState, v: Viewer): RaceRow[] {
  const { me, locale } = v;
  const name = (id: string) => playerName(locale, v.setup, id);
  const ids = state.players.map((p) => p.id);
  const n = ids.length;
  const countdown = (kind: CountdownVictoryId) => state.countdowns.find((c) => c.kind === kind);
  const countdownNote = (kind: CountdownVictoryId) => {
    const c = countdown(kind);
    return c
      ? t(locale, c.paused ? 'race.countdownPaused' : 'race.countdown', {
          name: name(c.playerId),
          time: clock(c.endsAt - state.tick),
        })
      : null;
  };

  // Economic: the reserve currency and a share of the system's output.
  const outputs = new Map(state.players.map((p) => [p.id, windowOutput(p.currency)]));
  const total = [...outputs.values()].reduce((a, b) => a + b, 0);
  const need = VICTORY.economicShare[n] ?? 0.5;
  const share = (id: string) => (total > 0 ? (outputs.get(id) ?? 0) / total : 0);
  const issuer = state.reserve.issuer;
  const sanctions = state.levers.find(
    (l) => l.kind === 'sanctions' && l.target === me && l.status === 'active',
  );
  let economicNote: string;
  let economicAlert = false;
  if (issuer === me && isSanctioned(state.levers, me) && sanctions) {
    economicNote = t(locale, 'race.economic.sanctioned', {
      time: clock(sanctions.until - state.tick),
    });
    economicAlert = true;
  } else if (issuer === me) economicNote = t(locale, 'race.economic.issuer');
  else if (issuer) economicNote = t(locale, 'race.economic.other', { name: name(issuer) });
  else economicNote = t(locale, 'race.economic.none');

  // Domination: most Lagrange stations standing and a vassal; in a duel, the rival as a vassal.
  const realm = (id: string) =>
    new Set([id, ...state.players.filter((p) => p.vassalOf === id).map((p) => p.id)]);
  const stations = state.outposts.filter((o) => o.kind === 'lagrangeStation');
  const held = (id: string) => stations.filter((o) => realm(id).has(o.owner)).length;
  const vassals = (id: string) => state.players.filter((p) => p.vassalOf === id).length;
  const domination = (id: string) =>
    n < 3
      ? vassals(id) / (n - 1)
      : Math.min(
          1,
          stations.length ? held(id) / stations.length / VICTORY.dominationLagrangeShare : 0,
        ) * (vassals(id) > 0 ? 1 : 0.9);

  // Technology: two tier-3 technologies, «Управление звездой», then the swarm's stages.
  const steps = (id: string) => {
    const p = state.players.find((x) => x.id === id)!;
    const swarm = state.megastructures.find((m) => m.owner === id)?.stages ?? 0;
    return (
      (Math.min(2, p.techs.filter((x) => (TIER3 as readonly string[]).includes(x)).length) +
        (p.techs.includes('starControl') ? 1 : 0) +
        swarm) /
      (3 + MEGASTRUCTURE.stages)
    );
  };
  const techLeader = [...ids].sort((a, b) => steps(b) - steps(a) || a.localeCompare(b))[0]!;

  // Joint: survive the final crisis; the Prestige leader wins.
  const leader = standings(state.players)[0]?.playerId ?? me;
  const stabilizer = state.finalStages.filter((o) => o !== 'failed').length;

  const progress = (f: (id: string) => number) =>
    ids.map((playerId) => ({ playerId, value: Math.max(0, f(playerId)) }));
  return [
    {
      id: 'economic',
      title: t(locale, 'race.economic'),
      status: t(locale, 'race.economic.status', {
        share: num(locale, share(me) * 100),
        need: num(locale, need * 100),
      }),
      progress: progress((id) => share(id) / need),
      note: countdownNote('economic') ?? economicNote,
      alert: economicAlert || (countdown('economic')?.playerId ?? me) !== me,
    },
    {
      id: 'domination',
      title: t(locale, 'race.domination'),
      status:
        n < 3
          ? t(locale, 'race.domination.duelStatus', { vassals: vassals(me), rivals: n - 1 })
          : t(locale, 'race.domination.status', { held: held(me), built: stations.length }),
      progress: progress(domination),
      note:
        countdownNote('domination') ??
        t(locale, n < 3 ? 'race.domination.duel' : 'race.domination.need'),
      alert: (countdown('domination')?.playerId ?? me) !== me,
    },
    {
      id: 'technological',
      title: t(locale, 'race.technological'),
      status: t(locale, 'race.technological.status', { name: name(techLeader) }),
      progress: progress(steps),
      note: countdownNote('technological') ?? t(locale, 'race.technological.need'),
      alert: (countdown('technological')?.playerId ?? me) !== me,
    },
    {
      id: 'joint',
      title: t(locale, 'race.joint'),
      status: t(locale, 'race.joint.status', {
        stages: stabilizer,
        total: CRISIS_DIRECTOR.finalPhases,
      }),
      progress: [],
      note: t(locale, 'race.joint.need', {
        name: name(leader),
        time: clock(CRISIS_DIRECTOR.finalWarningTick),
      }),
      alert: false,
    },
  ];
}

export interface PlayerRow {
  readonly id: string;
  readonly name: string;
  readonly faction: FactionId;
  readonly prestige: number;
  readonly rate: number;
  /** Short badges: war, sanctions, embargo, vassalage. */
  readonly relations: readonly { readonly text: string; readonly alert: boolean }[];
}

export function playerRows(state: GameState, v: Viewer): PlayerRow[] {
  const { me, locale } = v;
  const leverBy = (kind: string, by: string, target: string) =>
    state.levers.some(
      (l) => l.kind === kind && l.status === 'active' && l.by.includes(by) && l.target === target,
    );
  return standings(state.players).map(({ playerId }) => {
    const p = state.players.find((x) => x.id === playerId)!;
    const relations: { text: string; alert: boolean }[] = [];
    // Teams (#137): whose side everyone is on.
    if (p.team !== undefined)
      relations.push({ text: t(locale, 'rel.team', { n: p.team }), alert: false });
    if (playerId !== me) {
      if (atWar(state, me, playerId)) relations.push({ text: t(locale, 'rel.war'), alert: true });
      else if (
        state.pendingWars.some(
          (w) => (w.by === playerId && w.target === me) || (w.by === me && w.target === playerId),
        )
      ) {
        relations.push({ text: t(locale, 'rel.warComing'), alert: true });
      }
      if (leverBy('sanctions', playerId, me))
        relations.push({ text: t(locale, 'rel.sanctionsOnYou'), alert: true });
      if (leverBy('embargo', playerId, me))
        relations.push({ text: t(locale, 'rel.embargoOnYou'), alert: true });
      if (leverBy('embargo', me, playerId))
        relations.push({ text: t(locale, 'rel.yourEmbargo'), alert: false });
      if (leverBy('sanctions', me, playerId))
        relations.push({ text: t(locale, 'rel.yourSanctions'), alert: false });
    }
    if (p.vassalOf) {
      relations.push({
        text: t(locale, 'rel.vassalOf', { name: playerName(locale, v.setup, p.vassalOf) }),
        alert: p.vassalOf !== me && playerId === me,
      });
    }
    return {
      id: playerId,
      name: playerName(locale, v.setup, playerId),
      faction: p.faction,
      prestige: p.prestige,
      rate: p.currency.rate,
      relations,
    };
  });
}
